import type { AccountCampaign } from '../../types/accountCampaign';

// Ad Preview Mockups Stage 1 §13.13. Per-row eligibility gate for the
// bulk "Generate Previews" action. Unlike Refresh from Campaign (which
// merges Campaign content into rows and so REQUIRES a single shared
// Campaign), bulk preview is per-row independent — each row generates
// its own URL from its own Campaign content. So the cross-Campaign
// constraint was unnecessary friction.
//
// Eligibility per row (mirrors the server's pre-flight rejection list
// in bulkGeneratePreviewLinks):
//   - rowType === 'campaign' (unassigned / opt-out have nothing to preview)
//   - campaignId set
//   - not soft-deleted
//
// Returns the eligible row IDs (subset of selectedIds) + a reason for
// the disabled tooltip when none qualify. Empty selection produces a
// "select one or more" reason mirroring the refresh helper for
// consistency in BulkActionBar tooltips.

export type BulkPreviewGate = {
  eligibleIds: string[];
  reason: string | null;
};

export function resolveBulkPreviewTarget(
  selectedIds: string[],
  rows: AccountCampaign[],
): BulkPreviewGate {
  if (selectedIds.length === 0) {
    return { eligibleIds: [], reason: 'Select one or more rows.' };
  }
  const selectedSet = new Set(selectedIds);
  const eligible = rows.filter(
    (r) =>
      selectedSet.has(r.id) &&
      r.rowType === 'campaign' &&
      r.campaignId !== null &&
      r.deletedAt === null,
  );
  if (eligible.length === 0) {
    return {
      eligibleIds: [],
      reason: 'No selected rows have an assigned Campaign.',
    };
  }
  return { eligibleIds: eligible.map((r) => r.id), reason: null };
}
